/* eslint-disable import/prefer-default-export */
// This utility replaces $location.path() getter and setter.

// Code normalizes widget hash-style URLs to return same path value as web HTML5-style (regular) URLs,
// as was done with $location.path().

// Decoding on read, encoding on write, and adding a missing leading slash are part of
// the existing $location.path() contract.

// Call to flush() is need to get Angular routing to load the actual route.
// Typical flush call would be: defer(() => $rootScope.$apply())

// Going forward, react-router-dom hooks, `useLocation` and `useNavigate`, are preferred
// for existing and future components that are function-based (and not class-based as for all hooks).
// These hooks also encapsulate the normalization of URLs for widget vs web styles as $location did.
import { checkIfWidget } from '.';
import { getURLSearchParams } from './searchParamUtils';

const getWidgetPathname = () => {
  const { hash } = window.location;
  const widgetPathRegex = /[^#?]+/;
  /* istanbul ignore next */
  return widgetPathRegex.test(hash) ? widgetPathRegex.exec(hash)[0] : '';
};
const getLocationPath = () => {
  const path = checkIfWidget() ? getWidgetPathname() : window.location.pathname;
  return decodeURI(path);
};

const normalizePath = (path) => {
  const leadingSlash = /^\//;
  const normalizedPath = leadingSlash.test(path) ? path : `/${path}`;
  return checkIfWidget() ? `/#${normalizedPath}` : normalizedPath;
};

// eslint-disable-next-line no-return-assign
const setLocationPath = (path, flushRoute = () => {}) => {
  const normalizedPath = normalizePath(path);
  const encodedPath = encodeURI(normalizedPath);
  window.history.pushState({}, null, encodedPath);
  flushRoute();
};

// Handles Widget / React Router 7 paths via navigate,
// and Web / Angular.js paths via setLocationPath
const handleNavigate = (path, navigate, flushRoute = () => { }) => {
  if (navigate && typeof navigate === 'function') {
    navigate(path);
  } else {
    setLocationPath(path, flushRoute);
  }
};

// replaces $location.url() getter
const getLocationUrl = () => {
  const { pathname, search, hash } = window.location;
  const url = checkIfWidget() ? hash.substring(1) : `${pathname}${search}${hash}`;
  return decodeURI(url);
};

const buildUrlString = (path, params, hash) => {
  let url = '';
  let paramsString = params.toString();
  paramsString = paramsString ? `?${paramsString}` : paramsString;
  if (checkIfWidget() || !hash) {
    url = `${path}${paramsString}`;
  } else {
    url = `${path}${paramsString}${hash}`;
  }
  return url;
};
// replaces $location.url() setter
// allows for the persisting of search params and hash value of current URL.
// params are a URLSearchParams object and default to current URL.
const setLocationUrl = ({
  path,
  params = getURLSearchParams(),
  hash = window.location.hash,
  flushRoute = () => { },
}) => {
  const normalizedPath = normalizePath(path);
  const encodedPath = encodeURI(normalizedPath);
  const url = buildUrlString(encodedPath, params, hash);
  window.history.pushState({}, null, url);
  flushRoute();
};

// Handles Widget / React Router 7 paths via navigate,
// and Web / Angular.js paths via window.history
const handleNavigateWithParams = (path, params = getURLSearchParams(), navigate, flushRoute = () => { }) => {
  if (navigate && typeof navigate === 'function') {
    const encodedPath = encodeURI(path);
    const url = buildUrlString(encodedPath, params);
    navigate(url);
  } else {
    setLocationUrl({ path, params, flushRoute });
  }
};

// replaces $location.search() getter
const getSearchParamsAsObject = (searchParams = getURLSearchParams()) => Object.fromEntries(searchParams.entries());

export {
  getLocationPath,
  setLocationPath,
  getLocationUrl,
  setLocationUrl,
  getSearchParamsAsObject,
  handleNavigate,
  handleNavigateWithParams,
};
